import React, { useEffect, useMemo, useState } from "react";

import { useAppShell } from "../components/layout/AppShell";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { djangoApi } from "../services/djangoApi";

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString();
}

export default function JsonTemplatesPage({ apiKey }) {
  const { selectedOrgId } = useAppShell();
  const [templates, setTemplates] = useState([]);
  const [listStatus, setListStatus] = useState("idle");
  const [listError, setListError] = useState("");
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [jsonTemplateText, setJsonTemplateText] = useState(
    JSON.stringify({ document_type: "", fields: {} }, null, 2),
  );
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveError, setSaveError] = useState("");

  const canSubmit = useMemo(() => name.trim().length > 0, [name]);

  async function refreshTemplates() {
    if (!apiKey) return;
    setListError("");
    setListStatus("loading");
    try {
      const data = await djangoApi.listJsonTemplates(apiKey, selectedOrgId, 100);
      const items = Array.isArray(data?.results) ? data.results : [];
      setTemplates(items);
      setListStatus("ready");
    } catch (e) {
      setListStatus("error");
      setListError(
        e instanceof Error ? e.message : "Failed to load JSON templates",
      );
    }
  }

  useEffect(() => {
    if (!apiKey) return;
    refreshTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, selectedOrgId]);

  async function onSubmit(event) {
    event.preventDefault();
    setSaveError("");

    let jsonTemplate;
    try {
      jsonTemplate = JSON.parse(jsonTemplateText);
    } catch {
      setSaveError("JSON template must be valid JSON.");
      return;
    }

    setSaveStatus("saving");
    try {
      const payload = {
        name: name.trim(),
        instructions: instructions.trim(),
        json_template: jsonTemplate,
      };
      await djangoApi.createJsonTemplate(apiKey, selectedOrgId, payload);
      setSaveStatus("idle");
      setName("");
      setInstructions("");
      setJsonTemplateText(JSON.stringify({ document_type: "", fields: {} }, null, 2));
      refreshTemplates();
    } catch (e) {
      setSaveStatus("error");
      setSaveError(
        e instanceof Error ? e.message : "Failed to save JSON template",
      );
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Templates</CardTitle>
            <Button
              type="button"
              variant="outline"
              onClick={refreshTemplates}
              disabled={listStatus === "loading"}
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {listError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {listError}
            </div>
          ) : null}

          {templates.length === 0 && listStatus !== "loading" ? (
            <div className="text-sm text-muted-foreground">
              No templates yet.
            </div>
          ) : null}

          <div className="max-h-[640px] space-y-3 overflow-auto pr-1">
            {templates.map((template) => (
              <div key={template.ulid} className="rounded-lg border bg-background p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {template.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatTimestamp(template.updated_date)}
                    </div>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground">
                    {template.ulid}
                  </div>
                </div>
                <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                  <div className="whitespace-pre-wrap">
                    {template.instructions || "(no instructions)"}
                  </div>
                </div>
                <div className="mt-3">
                  <pre className="mt-2 max-h-[220px] overflow-auto rounded-md border bg-muted p-3 text-xs">
                    {JSON.stringify(template.json_template, null, 2)}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="h-fit">
        <CardHeader>
          <CardTitle>Create</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="templateName">Name</Label>
              <Input
                id="templateName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Invoice baseline"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateInstructions">Instructions</Label>
              <Textarea
                id="templateInstructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Instructions..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateJson">JSON Structure</Label>
              <Textarea
                id="templateJson"
                value={jsonTemplateText}
                onChange={(e) => setJsonTemplateText(e.target.value)}
                className="min-h-[220px] font-mono"
                placeholder='{"field": ""}'
              />
            </div>

            {saveError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {saveError}
              </div>
            ) : null}

            <Button type="submit" disabled={!canSubmit || saveStatus === "saving"}>
              {saveStatus === "saving" ? "Saving..." : "Save"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
